import {Component, OnInit, ViewChild} from '@angular/core';
import {MatPaginator} from '@angular/material/paginator';
import {FabMessageService} from '@fabric/components/message';
import {FabThemeService} from '@fabric/components/theme';
import {PersonalService} from './personal.service';
import {AuthenticationService} from '../core/authentication/authentication.service';
import {Vacation} from '../admin/models/vacation.model';
import {Employee} from '../admin/models/employee.model';
import {MatDialog} from '@angular/material/dialog';
import {PersonalPopUpComponent} from './personal-pop-up/personal-pop-up.component';
import {MatTableDataSource} from '@angular/material/table';
import {CancelPopUpComponent} from '../cancel-pop-up/cancel-pop-up.component';
import {ExcelUtil} from '../common/web-workers/excel-export.worker';
import {writeFile} from 'xlsx';
import {MatSort} from '@angular/material/sort';


@Component({
    selector: 'app-admin',
    templateUrl: './personal.component.html',
    styleUrls: ['./personal.component.scss']
})
export class PersonalComponent implements OnInit {

    @ViewChild(MatPaginator, {static: false})
    set paginator(value: MatPaginator) {
        if (this.dataSource) {
            this.dataSource.paginator = value;
        }
    };

    @ViewChild('sort', {read: MatSort, static: false})
    set sort(value: MatSort) {
        if (this.dataSource) {
            this.dataSource.sort = value;
        }
    };

    public dataSource: MatTableDataSource<Vacation> = new MatTableDataSource<Vacation>([]);
    public isRefresh = false;
    public isPageLoading = false;
    public isExcelExporting = false;
    public vacation = {} as Vacation;
    public gminValue: string = '';
    public employee = {} as Employee;
    public fieldNames = ['startDate', 'endDate', 'halfDay', 'numberOfAnnualLeaveDays', 'dateCreated', 'absenceType',
        'absenceReason', 'requestStatus', 'modify', 'cancel'];
    public displayNames = ['Start Date', 'End Date', 'Half Day', 'Total Absences', 'Date Created', 'Absence Type',
        'Absence Reason', 'Request Status', 'Edit', 'Cancel'];
    public totalVacationAllowance: number = 0;
    public remainingVacationAllowance: number = 0;
    public pendingVacationDays: number = 0;
    public pendingVacationRequests: number = 0;
    public carryover: number = 0;
    public placeholderYear: number = 0;
    public yearHolderValue: number = 0;
    public filterYears: number[] = [];
    public countrySelected: any;
    public isValid = true;
    public isDate = true;

    constructor(private personalService: PersonalService,
                private messageService: FabMessageService,
                private authService: AuthenticationService,
                private dialog: MatDialog,
                public readonly themeService: FabThemeService) {
    }

    ngOnInit() {
        this.gminValue = this.authService.userInfo.gmin || '';
        this.placeholderYear = new Date().getFullYear();
        this.countrySelected = localStorage.getItem('CountryCode');
        this.personalService.fetchEmployeeByGmin(this.gminValue).subscribe({
            next: (data) => {
                this.employee = data;
                this.sortYear();
                this.displayDataOnTable(this.placeholderYear);
                this.isPageLoading = false;
                this.isRefresh = false;
                this.isValid = true;
            },
            error: () => {
                this.messageService.displayErrorMessage('Invalid GMIN');
                this.isPageLoading = false;
                this.isValid = false;
            }
        });
        this.isPageLoading = true;
    }

    sortPersonalStartDate() {
        for (const element of this.dataSource.data) {
            if (element.startDate.includes('/')) {
                const startDate = element.startDate;
                const endDate = element.endDate;
                const [day, month, year] = startDate.split('/');
                const [dayEnd, monthEnd, yearEnd] = endDate.split('/');
                element.startDate = new Date(+year, +month - 1, +day).toISOString();
                element.endDate = new Date(+yearEnd, +monthEnd - 1, +dayEnd).toISOString();
            }
        }
    }

    dateCheck() {
        for (const element of this.dataSource.data) {
            this.isDate = !element.startDate.includes('/');
        }
    }

    public refreshTable(): void {
        this.isRefresh = false;
        this.personalService.fetchEmployeeByGmin(this.gminValue).subscribe({
            next: (data) => {
                this.employee = data;
                this.sortYear();
                this.displayDataOnTable(this.yearHolderValue);
                this.isRefresh = false;
            },
            error: () => {
                this.isRefresh = false;
                this.messageService.displayErrorMessage('Error fetching data');
            }
        });
        this.isRefresh = true;
    }

    /**
     * Loops through the employee's vacation requests and adds those created in the passed in year to the datasource
     * @param year The year to filter for
     */
    public displayDataOnTable(year: number) {
        this.yearHolderValue = year;
        const tempDataSource: MatTableDataSource<Vacation> = new MatTableDataSource<Vacation>([]);
        this.employee.vacation.forEach((value) => {
            let tempDate;
            if (!value.endDate.includes('/')) {
                tempDate = this.formatDate(value.endDate);
            } else {
                tempDate = value.endDate;
            }
            tempDate = this.personalService.swapDateAndMonth(tempDate);
            if (Number(this.personalService.getYearOfDateString(tempDate)) == year) {
                tempDataSource.data.push(value);
            }
        });
        if (tempDataSource.data !== null) {
            this.dataSource.data = tempDataSource.data;
        } else {
            this.dataSource.data = [];
        }
        this.employeeLeaveDetails(year);
        this.sortPersonalStartDate();
        this.dateCheck();
    }

    /**
     * Takes a string, number or Date object as input and
     * returns a formatted date string in the format 'DD/MM/YYYY'.
     * It pads the day and month with a leading zero if necessary
     * @param dateString
     */
    formatDate(dateString: string | number | Date) {
        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    }

    /**
     * Loops through the employee vacation allowance and assigns values from the current year to variables
     * @param year The year to find values for
     */
    public employeeLeaveDetails(year: number) {
        this.totalVacationAllowance = 0;
        this.remainingVacationAllowance = 0;
        this.pendingVacationRequests = 0;
        this.pendingVacationDays = 0;
        this.carryover = 0;
        this.employee.employeeVacationAllowance.forEach((i) => {
            if (i.year == year) {
                this.totalVacationAllowance = i.vacationAllowance;
                this.remainingVacationAllowance = i.vacationAllowanceRemaining;
                this.pendingVacationRequests = this.personalService.pendingVacationRequests(this.employee, year);
                this.pendingVacationDays = this.personalService.pendingVacationDays(this.employee, year);
                this.carryover = i.vacationAllowanceCarryover;
            }
        });
        if (this.totalVacationAllowance == 0) {
            this.initialiseEmployeeVacationAllowance(year);
        }
    }

    public initialiseEmployeeVacationAllowance(year: number) {
        this.totalVacationAllowance = 0;
        this.remainingVacationAllowance = 0;
        this.pendingVacationRequests = this.personalService.pendingVacationRequests(this.employee, year);
        this.pendingVacationDays = this.personalService.pendingVacationDays(this.employee, year);
        this.carryover = 0;
    }

    /**
     * Formats date values for each vacation request, adds each allowance year to an array and sorts these
     */
    public sortYear() {
        this.employee.vacation.forEach((i) => {
            i.dateCreated = this.personalService.convertDateFormat(i.dateCreated);
            i.updatedTimestamp = this.personalService.convertDateFormat(i.updatedTimestamp);
            i.createdTimestamp = this.personalService.convertDateFormat(i.createdTimestamp);
            i.approvalDate = this.personalService.convertDateFormat(i.approvalDate);
            const eachYearString = this.personalService.getYearOfDateString(this.personalService.swapDateAndMonth(i.endDate));
            this.filterYears.push(Number(eachYearString));
        });
        this.filterYears = this.personalService.getUniqueValues(this.filterYears).sort();
    }

    /**
     * Opens a popup to modify a vacation request
     * @param employee The employee the vacation request is for (to get gmin, etc)
     * @param vacation The vacation request to modify
     */
    public openModifyPopup(employee: Employee, vacation: Vacation): void {
        this.isDate = false;
        this.dialog.open(PersonalPopUpComponent, {data: {employee, vacation}}).afterClosed().subscribe(() => {
            this.refreshTable();
        });
    }

    /**
     * Opens a popup to cancel a vacation request
     * @param vacationID The vacation request to be cancelled
     */
    public openCancelPopup(vacationID: string): void {
        this.dialog.open(CancelPopUpComponent, {
            data: {
                vacationID,
                requestType: 'PERSONAL'
            }
        }).afterClosed().subscribe(() => {
            this.refreshTable();
        });
    }

    /**
     * Opens a popup to cancel a vacation request
     * @param vacation The vacation request to be cancelled
     */
    public openRequestCancelPopup(vacation: Vacation): void {
        this.dialog.open(CancelPopUpComponent, {
            data: {
                vacation,
                requestType: 'PERSONAL',
                requestStatus: 'REQUEST_CANCEL'
            }
        }).afterClosed().subscribe(() => {
            this.refreshTable();
        });
    }

    /**
     * Opens a popup to add a new vacation request
     * @param employee The employee the vacation request is for (to get gmin, etc)
     */
    public openPersonalPopup(employee: Employee): void {
        this.dialog.open(PersonalPopUpComponent, {data: {employee, vacation: null}}).afterClosed().subscribe(() => {
            this.refreshTable();
        });
    }

    /**
     * Calls a service method to get a string representing a boolean value
     * @param bool The boolean value to check
     */
    public halfDayStatus(bool: boolean): string {
        return this.personalService.halfDayStatus(bool);
    }

    /**
     * Exports an excel of the table data
     */
    public exportExcel(): void {

        if (this.dataSource.data.length > 0) {
            this.isExcelExporting = true;
            let updatedVacationExportData = [];
            for (let vacationData of this.dataSource.data) {
                const startDate = new Date(vacationData['startDate']);
                const startDateString = startDate.getFullYear() + '-' +
                    ('00' + (startDate.getMonth() + 1)).slice(-2) + '-' +
                    ('00' + startDate.getDate()).slice(-2);
                const endDate = new Date(vacationData['endDate']);
                const endDateString = endDate.getFullYear() + '-' +
                    ('00' + (endDate.getMonth() + 1)).slice(-2) + '-' +
                    ('00' + endDate.getDate()).slice(-2);

                updatedVacationExportData.push({
                    'startDate': startDateString,
                    'endDate':endDateString,
                    'halfDay': (vacationData['halfDay'] ? "Yes" : "No"),
                    'halfDayType': vacationData['halfDayType'],
                    'dateCreated': vacationData['dateCreated'],
                    'approvalDate': vacationData['approvalDate'],
                    'absenceReason':vacationData['absenceReason'],
                    'requestStatus':vacationData['requestStatus'],
                    'absenceType': vacationData['absenceType'],
                    'numberOfAnnualLeaveDays':vacationData['numberOfAnnualLeaveDays']

                });
            }
            ExcelUtil.exportObjectArrayMultiThreaded(updatedVacationExportData, 'Vacations')
                .subscribe({
                    next: (workbook) => {
                        if (workbook) {
                            writeFile(workbook, 'Vacations.xlsx');
                            this.isExcelExporting = false;
                        }
                    },
                    error: () => {
                        this.messageService.displayErrorMessage('Failed to export Excel file');
                        this.isExcelExporting = false;
                    }
                });
        } else {
            this.messageService.displayErrorMessage('No data to export');
        }
    }
}
